import React, { useState } from "react";
import Links from "../Links";
import Images from "../Images";
import { LayoutDashboard, ChevronLeft, ChevronRight } from "lucide-react";
import tw from "tailwind-styled-components";
import { NavLink, useNavigate } from "react-router-dom";
import { logoutUser } from "../requests/auth/Authentication";
import { useDispatch } from "react-redux";
import { logout } from "../redux/store";
import PermissionAwareNavItem from "./Permissions/PermissionAwareNavItem";

const Sidebar = ({ onToggleCollapse }) => {
  // Filter out subscription-related links
  const links = Links.filter(
    (link) =>
      link.link !== "/subscription" &&
      link.link !== "/pricing" &&
      link.name !== "Manage Subscription" &&
      link.name !== "Subscription Plans"&&
      link.name !== "Manage Sender-ID"
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onToggleCollapse(newState);
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logoutUser();
      dispatch(logout());
      // The logout function in Redux store will handle navigation to landing page
    } catch (error) {
      console.error("Logout failed:", error);
      // If logout fails, still navigate to landing page
      window.location.href = "/";
    } finally {
      setLoading(false);
    }
  };

  const comingSoonLinks = ["Help & Support"];

  return (
    <div
      className={`fixed flex flex-col h-screen text-white bg-white shadow-lg transition-all duration-300 ease-in-out z-50 ${
        isCollapsed ? "w-24" : "w-72"
      }`}
    >
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        {!isCollapsed ? (
          <img src={Images.logo} alt="Dealboxx - Logo" className="w-full" />
        ) : (
          <img
            src={Images.shortlogo}
            alt="Dealboxx - Short Logo"
            className="w-12 h-10"
          />
        )}

        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className="text-gray-800 hover:text-[#FF6C6C] transition"
        >
          {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </button>
      </div>

      {/* Navigation */}
      <nav
        className={`flex-1 mt-4 overflow-y-auto transition-all duration-300 ${
          isCollapsed ? "px-2" : "px-6"
        }`}
      >
        <ul className="space-y-2">
          <li>
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `group flex items-center w-full gap-2 rounded-md p-4 font-semibold text-sm transition duration-300 ease-in-out cursor-pointer ${
                  isCollapsed ? "justify-center px-2" : ""
                } ${
                  isActive
                    ? "bg-[#FF6C6C] text-white"
                    : "text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                }`
              }
              title={isCollapsed ? "Dashboard" : ""}
            >
              <LayoutDashboard className="flex-shrink-0 transition duration-300 group-hover:text-white" />
              <p
                className={`text-lg font-semibold transition-all duration-300 ${
                  isCollapsed ? "overflow-hidden w-0 opacity-0" : "opacity-100"
                }`}
              >
                Dashboard
              </p>
            </NavLink>
          </li>

          {isCollapsed && (
            <div className="space-y-2">
              {links.map((link, index) => (
                <PermissionAwareNavItem
                  key={`collapsed-${index}`}
                  link={link}
                  isCollapsed={true}
                  isMobile={false}
                  comingSoonLinks={comingSoonLinks}
                  onLogout={handleLogout}
                />
              ))}
            </div>
          )}

          {/* Expanded view - show grouped links */}
          {!isCollapsed && (
            <>
              <LinkGroup
                title="Deal Management"
                links={links.filter((link) => link.type === "Deals")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
              <LinkGroup
                title="Order Management"
                links={links.filter((link) => link.type === "Orders")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
              <LinkGroup
                title="Branches"
                links={links.filter((link) => link.type === "Branches")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
              <LinkGroup
                title="Vendor Partners"
                links={links.filter((link) => link.type === "Partners")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
              <LinkGroup
                title="Affiliates"
                links={links.filter((link) => link.type === "Affiliates")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
              <LinkGroup
                title="SMS Campaign Management"
                links={links.filter((link) => link.type === "Campaigns")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
              <LinkGroup
                title="Accounts & Settings"
                links={links.filter((link) => link.type === "Settings")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
              <LinkGroup
                title="Profile"
                links={links.filter((link) => link.type === "Help")}
                comingSoonLinks={comingSoonLinks}
                handleLogout={handleLogout}
              />
            </>
          )}
        </ul>
      </nav>

      {loading && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="w-12 h-12 border-4 border-white border-t-[#FF6C6C] rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

const LinkGroup = ({ title, links, comingSoonLinks, handleLogout }) => (
  <div className="flex flex-col">
    <LinkGroupTitle>{title}</LinkGroupTitle>
    {links.map((link, index) => (
      <PermissionAwareNavItem
        key={index}
        link={link}
        isCollapsed={false}
        isMobile={false}
        comingSoonLinks={comingSoonLinks}
        onLogout={handleLogout}
      />
    ))}
  </div>
);

const LinkGroupTitle = tw.p`text-sm font-bold text-black mt-3 p-3 w-full`;

export default Sidebar;
